import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Typography,
  Card,
  message,
  Tooltip,
  Row,
  Col,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import {
  getCaseCategoriesApi,
  createCaseCategoryApi,
  updateCaseCategoryApi,
  deleteCaseCategoryApi,
} from "../../../../api/caseCategory.api";
import { useAuth } from "../../../../context/AuthContext";

const { Title, Text } = Typography;

const CaseCategory = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("case_categories.write");

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [tableLoading, setTableLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchCategories = useCallback(async (page = 1, limit = 10, q = "") => {
    setTableLoading(true);
    try {
      const { data } = await getCaseCategoriesApi(page, limit, q);
      setItems(data.data.items || []);
      setPagination((prev) => ({
        ...prev,
        current: data.data.pagination.page,
        total: data.data.pagination.total,
        pageSize: data.data.pagination.limit,
      }));
    } catch (err) {
      message.error(
        err.response?.data?.message || "Failed to load case categories."
      );
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories(1, pagination.pageSize, searchText);
  }, [fetchCategories, pagination.pageSize, searchText]);

  const handleCreate = async (values) => {
    setCreateLoading(true);
    try {
      await createCaseCategoryApi({ category_name: values.category_name });
      message.success("Case category created.");
      setCreateOpen(false);
      createForm.resetFields();
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        createForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] }))
        );
      } else {
        message.error(err.response?.data?.message || "Create failed.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const openEdit = (row) => {
    setSelected(row);
    editForm.setFieldsValue({
      category_name: row.category_name,
    });
    setEditOpen(true);
  };

  const handleUpdate = async (values) => {
    if (!selected?.category_id) return;
    setEditLoading(true);
    try {
      await updateCaseCategoryApi(selected.category_id, {
        category_name: values.category_name,
      });
      message.success("Case category updated.");
      setEditOpen(false);
      setSelected(null);
      editForm.resetFields();
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      const backendErrors = err.response?.data?.errors;
      if (backendErrors?.length) {
        editForm.setFields(
          backendErrors.map((e) => ({ name: e.field, errors: [e.message] }))
        );
      } else {
        message.error(err.response?.data?.message || "Update failed.");
      }
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await deleteCaseCategoryApi(row.category_id);
      message.success("Case category deleted.");
      fetchCategories(pagination.current, pagination.pageSize, searchText);
    } catch (err) {
      message.error(err.response?.data?.message || "Delete failed.");
    }
  };

  const handleSearch = (value) => {
    const next = String(value || "").trim();
    setSearchText(next);
    fetchCategories(1, pagination.pageSize, next);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "category_id",
      width: 80,
      align: "center",
    },
    {
      title: "Category name",
      dataIndex: "category_name",
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      render: (_, r) =>
        canWrite ? (
          <Space>
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                onClick={() => openEdit(r)}
                style={styles.ghostBtn}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this category?"
              onConfirm={() => handleDelete(r)}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <Helmet>
          <title>Case category - SCMS</title>
          <meta name="description" content="Case categories" />
        </Helmet>
        <div className="content container-fluid">
          {/* <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">মামলার ক্যাটাগরি</h3>
               
              </div>
            </div>
          </div> */}

          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: 20, padding: "10px 10px" }}
          >
            <Col>
              <Space>
                <FolderOpenOutlined style={{ fontSize: 22, color: "black" }} />
                <Title level={4} style={{ margin: 0, color: "black" }}>
                  মামলার ক্যাটাগরি
                </Title>
                <Tag color="blue">{pagination.total} total</Tag>
              </Space>
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() =>
                    fetchCategories(
                      pagination.current,
                      pagination.pageSize,
                      searchText
                    )
                  }
                  style={styles.ghostBtn}
                />
                <Input.Search
                  allowClear
                  placeholder="Search by name or ID"
                  onSearch={handleSearch}
                  onChange={(e) => {
                    if (!e.target.value) handleSearch("");
                  }}
                  enterButton={<SearchOutlined />}
                  style={{ width: 260 }}
                />
                {canWrite && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateOpen(true)}
                    style={styles.primaryBtn}
                  >
                    Add category
                  </Button>
                )}
              </Space>
            </Col>
          </Row>

          <Card style={styles.tableCard} bodyStyle={{ padding: 0 }}>
            <Table
              columns={columns}
              dataSource={items}
              rowKey="category_id"
              loading={tableLoading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                onChange: (page, pageSize) =>
                  fetchCategories(page, pageSize, searchText),
                style: { padding: "16px 24px" },
              }}
              scroll={{ x: 480 }}
            />
          </Card>
        </div>

        <Modal
          title="Add case category"
          open={createOpen}
          onCancel={() => {
            setCreateOpen(false);
            createForm.resetFields();
          }}
          footer={null}
          width={480}
        >
          <Form
            form={createForm}
            layout="vertical"
            onFinish={handleCreate}
            style={{ marginTop: 12 }}
          >
            <Form.Item
              name="category_name"
              label="Category name"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input placeholder="Enter category name" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={createLoading}>
                  Create
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Edit case category"
          open={editOpen}
          onCancel={() => {
            setEditOpen(false);
            setSelected(null);
            editForm.resetFields();
          }}
          footer={null}
          width={480}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdate}
            style={{ marginTop: 12 }}
          >
            <Form.Item
              name="category_name"
              label="Category name"
              rules={[{ required: true, message: "Required" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={() => setEditOpen(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={editLoading}>
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

const styles = {
  tableCard: {
    borderRadius: 12,
    overflow: "hidden",
  },
  primaryBtn: {
    background: "rgb(120, 57, 120)",
    border: "none",
    borderRadius: 8,
  },
  ghostBtn: {
    background: "transparent",
    border: "1px solid #334155",
    borderRadius: 8,
  },
};

export default CaseCategory;
